import React from "react";
import { GetServerSidePropsContext, Page } from "next";
import { useTranslation } from "@finbackoffice/site-core";
import { PopupTags, SiteContentScopes, TranslationScopes } from "@finbackoffice/enums";
import { IBannerResponse } from "@finbackoffice/clientbff-client";
import getBonusesLayout from "components/layout/bonuses/BonusesLayout";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import Seo from "components/base/seo/Seo";
import {
    clientBFFSingleton,
    getServerData,
    createGetServerSideProps,
    CommonSsrProps,
    getServerRuntimeConfig,
} from "services/ssr";
import BonusesPromotions from "components/pages/bonuses/promotions/BonusesPromotions";
import { getCurrentLocale } from "utils/helpers";

type IProps = {
    banners: IBannerResponse[];
};

export const getServerSideProps = createGetServerSideProps<IProps & CommonSsrProps>(
    async (context: GetServerSidePropsContext) => {
        const runtimeConfig = await getServerRuntimeConfig();
        const client = clientBFFSingleton.getInstance(context.req.cookies.jwt);
        const locale = getCurrentLocale(runtimeConfig, context.locale);

        const [{ translations, configs, profile, popupBanners, banners }] = await Promise.all([
            getServerData(runtimeConfig, context, client, {
                popupBanners: { scope: [PopupTags.Bonuses], device: PopupTags.Desktop },
                translationScope: [TranslationScopes.Bonuses, TranslationScopes.BonusesPromotions],
                banners: {
                    scope: [SiteContentScopes.Bonuses],
                },
            }),
        ]);

        const props = {
            configs,
            runtimeConfig,
            jwt: context.req.cookies.jwt || "",
            profile: profile || null,
            locale,
            translations,
            banners: banners.items,
            popupBanners: popupBanners || [],
        };

        return {
            props,
        };
    },
);

const BonusesPromotionsPage: Page = () => {
    const { t } = useTranslation();
    return (
        <ErrorBoundary name={BonusesPromotions.name}>
            <Seo
                page={TranslationScopes.BonusesPromotions}
                replace={{
                    title: {
                        page_name: t("pageName_bonuses_promotions"),
                    },
                }}
            />
            <BonusesPromotions />
        </ErrorBoundary>
    );
};

BonusesPromotionsPage.getLayout = getBonusesLayout;

export default BonusesPromotionsPage;
